import history from '@history';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { ComposedHeader, TextWithLabel } from '@main/components';
import { useRequest } from '@main/hooks';
import { getBillingInfo } from 'app/api/company/billing';
import { useContext, useEffect, useState } from 'react';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { useDispatch, useSelector } from 'react-redux';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { hasScope } from 'src/app/scopes/scopes';
import { showMessage } from 'app/store/core/messageSlice';
import { useParams } from 'react-router-dom';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const BillingInformation = () => {
  const { config } = useSelector((state) => state.config);
  const { scopes: selfScopes } = useSelector((state) => state.profile);

  const Dispatch = useDispatch();

  const { finalizeSession } = useContext(BootstrapContext);
  const { id: previousId } = useParams();
  const { host } = config.api;
  const [data, setData] = useState(null);

  const handleOnForbidden = async () => {
    await finalizeSession();
  };
  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const { makeRequest: getData, response: responseGetData } = useRequest(getBillingInfo, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });

  useEffect(() => {
    if (!previousId) return;
    getData(previousId);
  }, [previousId]);

  useEffect(() => {
    if (responseGetData === undefined) return;
    console.log(responseGetData);
    setData(responseGetData);
  }, [responseGetData]);

  const handleUpdate = () => {
    history.push(`/companies/info/${previousId}/tax-information`);
  };

  const emptyText = 'No hay datos para mostrar';

  const hasValue = (value) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  };

  const getCombinedValue = (...values) => {
    const normalizedValues = values
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter((value) => hasValue(value));

    return normalizedValues.length > 0 ? normalizedValues.join(' - ') : null;
  };

  const getPhoneValue = () => {
    const phone = data?.responsiblePhone?.trim();
    const phoneCode = data?.responsiblePhoneCode?.trim();

    if (!phone) return null;

    const normalizedCode = phoneCode
      ? phoneCode.startsWith('+')
        ? phoneCode
        : `+${phoneCode}`
      : '+52';

    return `${normalizedCode} ${phone}`;
  };

  const getFields = (fields) => fields.filter(({ value }) => hasValue(value));

  const renderFields = (fields) => {
    if (fields.length === 0) {
      return <Typography className="text-gray-500 mb-24">{emptyText}</Typography>;
    }

    return fields.map(({ title, value }) => (
      <TextWithLabel key={title} title={title}>
        {value}
      </TextWithLabel>
    ));
  };

  const contactFields = getFields([
    {
      title: 'Nombre del responsible',
      value: data?.responsibleName,
    },
    {
      title: 'Correo electronico',
      value: data?.responsibleEmail,
    },
    {
      title: 'Telefono',
      value: getPhoneValue(),
    },
  ]);

  const fiscalFields = getFields([
    {
      title: 'Regimen fiscal',
      value: getCombinedValue(data?.fiscalRegime, data?.fiscalRegimeName),
    },
    {
      title: 'RFC',
      value: data?.rfc,
    },
    {
      title: 'Razon Social',
      value: data?.businessName,
    },
    {
      title: 'Uso de CFDI',
      value: getCombinedValue(data?.cfdiUse, data?.cfdiUseName),
    },
    {
      title: 'Metodo de pago',
      value: getCombinedValue(data?.paymentMethod, data?.paymentMethodName),
    },
  ]);

  const addressFields = getFields([
    {
      title: 'Calle',
      value: data?.addressStreet,
    },
    {
      title: 'No. Exterior',
      value: data?.addressExtNumber,
    },
    {
      title: 'No. Interior',
      value: data?.addressIntNumber,
    },
    {
      title: 'Colonia',
      value: data?.addressColony,
    },
    {
      title: 'Alcaldia o Municipio',
      value: data?.addressCity,
    },
    {
      title: 'Estado',
      value: data?.addressStateName,
    },
    {
      title: 'Codigo Postal',
      value: data?.addressZip,
    },
    {
      title: 'Pais',
      value: data?.addressCountryName,
    },
  ]);

  return (
    <Root
      header={
        <ComposedHeader
          title={`Datos de Facturacion: ${data?.companyName || emptyText}`}
          decorator="button"
        />
      }
      content={
        <Grid container spacing={0} className="p-20">
          <Grid item xs={12}>
            <Card className="flex flex-auto max-w-5xl mx-auto">
              <CardContent className="w-full">
                <div className="p-16 mb-20">
                  <Grid spacing={6} container>
                    <Grid xs={12} md={6} item>
                      <Typography className="text-2xl font-semibold leading-tight mb-8">
                        Datos de contacto
                      </Typography>
                      <Divider className="mb-20" />
                      {renderFields(contactFields)}
                      <Typography className="text-2xl font-semibold leading-tight mb-8">
                        Datos fiscales
                      </Typography>
                      <Divider className="mb-20" />
                      {renderFields(fiscalFields)}
                    </Grid>
                    <Grid xs={12} md={6} item>
                      <Typography className="text-2xl font-semibold leading-tight mb-8">
                        Domicilio Fiscal
                      </Typography>
                      <Divider className="mb-20" />
                      {renderFields(addressFields)}
                    </Grid>
                  </Grid>
                </div>
                <div className="flex items-center justify-center w-full pb-16">
                  {hasScope(selfScopes, 'Company', 'update') && (
                    <Button
                      onClick={handleUpdate}
                      startIcon={<EditOutlinedIcon />}
                      variant="contained"
                      color="primary"
                    >
                      Editar Informacion
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default BillingInformation;
